import React, { useEffect, useState } from "react";
import CustomDropdown from "../common/CustomDropdown";
import {
  getPositionsForCategory,
  FEED_TYPE_FILTER_CHOICES,
} from "../../constants/agentTenderChoices";

// N2 filter modal — the backend-supported club filters (position / country /
// request type + verified). Deliberately leaner than the agent
// AgentTenderFilterModal (no market-value/division/nationality). The design's
// Window filter is omitted: the backend feed has no transfer_window filter param
// (window is read-only in the payload), so a Window control would be a dead knob.
//
// Country options are passed in from the feed (distinct values of the loaded
// requests) so no extra endpoint is needed; all filters derive from the club
// server-side WITHOUT resolving the club (anonymity invariant).
const ClubNetworkFilterModal = ({
  isOpen,
  onClose,
  onApply,
  initialFilters,
  countryOptions = [],
}) => {
  const [filters, setFilters] = useState(initialFilters);

  useEffect(() => {
    if (isOpen) setFilters(initialFilters);
  }, [isOpen, initialFilters]);

  if (!isOpen) return null;

  const set = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const positionOptions = [
    { value: "", label: "All positions" },
    ...getPositionsForCategory("player"),
  ];
  const countryOpts = [{ value: "", label: "All countries" }, ...countryOptions];

  const dropdownCls = "w-full px-3 py-2 border rounded-lg";

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  const handleReset = () =>
    setFilters({
      position: "",
      country: "",
      type_of_tender: "",
      verified: false,
    });

  const Field = ({ label, children }) => (
    <div className="mb-5">
      <h3 className="text-sm font-medium mb-2">{label}</h3>
      {children}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white rounded-t-2xl sm:rounded-lg p-6 w-full sm:max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-xl font-semibold">Filter requests</h2>
          <button onClick={onClose} className="text-secondary hover:text-gray-900">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <Field label="Position">
          <CustomDropdown
            options={positionOptions}
            value={filters.position || ""}
            onChange={(v) => set("position", v)}
            placeholder="All positions"
            displayProperty="label"
            valueProperty="value"
            searchEnabled
            searchPlaceholder="Search position..."
            className={dropdownCls}
          />
        </Field>

        <Field label="Country">
          <CustomDropdown
            options={countryOpts}
            value={filters.country || ""}
            onChange={(v) => set("country", v)}
            placeholder="All countries"
            displayProperty="label"
            valueProperty="value"
            searchEnabled
            searchPlaceholder="Search country..."
            className={dropdownCls}
          />
        </Field>

        <Field label="Request type">
          <CustomDropdown
            options={FEED_TYPE_FILTER_CHOICES}
            value={filters.type_of_tender || ""}
            onChange={(v) => set("type_of_tender", v)}
            placeholder="All types"
            displayProperty="label"
            valueProperty="value"
            className={dropdownCls}
          />
        </Field>

        <label className="flex items-center gap-2 mb-6">
          <input
            type="checkbox"
            checked={!!filters.verified}
            onChange={(e) => set("verified", e.target.checked)}
            className="rounded"
          />
          <span className="text-sm">Show club-verified only</span>
        </label>

        <div className="flex gap-3">
          <button
            onClick={handleReset}
            className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50"
          >
            Reset
          </button>
          <button onClick={onClose} className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={handleApply}
            className="flex-1 px-4 py-2 bg-[#2283FF] text-white rounded-lg hover:bg-blue-600"
          >
            Apply filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClubNetworkFilterModal;
